import * as React from 'react'
import { motion } from 'framer-motion'
import { cn } from '../../utils/cn'

/**
 * JPG Academy Toggle / Switch
 *
 * A toggle switch for boolean settings. Gold knob, navy track.
 * Always animated, respects prefers-reduced-motion via framer-motion.
 *
 * Usage:
 *   <Toggle checked={isEnabled} onChange={setIsEnabled} label="Enable notifications" />
 *   <Toggle checked={dark} onChange={setDark} size="sm" />
 */

export interface ToggleProps extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'onChange'> {
	/** Whether the toggle is on */
	checked?: boolean
	/** Change handler */
	onChange?: (checked: boolean) => void
	/** Label text */
	label?: string
	/** Helper text */
	hint?: string
	/** Size */
	size?: 'sm' | 'md' | 'lg'
	/** Disabled state */
	disabled?: boolean
}

const sizeStyles = {
	sm: { track: 'w-8 h-[18px]', knob: 'w-3.5 h-3.5', translate: 15 },
	md: { track: 'w-10 h-[22px]', knob: 'w-[18px] h-[18px]', translate: 19 },
	lg: { track: 'w-12 h-[26px]', knob: 'w-[22px] h-[22px]', translate: 23 },
}

export function Toggle({
	checked = false,
	onChange,
	label,
	hint,
	size = 'md',
	disabled = false,
	className,
	...props
}: ToggleProps) {
	const s = sizeStyles[size]
	const id = React.useId()

	return (
		<div className={cn('flex items-center gap-3', className)}>
			<button
				id={id}
				type="button"
				role="switch"
				aria-checked={checked}
				aria-label={!label ? 'Toggle' : undefined}
				disabled={disabled}
				onClick={() => !disabled && onChange?.(!checked)}
				className={cn(
					'relative inline-flex items-center rounded-full p-0.5',
					'transition-colors duration-fast ease-default',
					'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold/40 focus-visible:ring-offset-2',
					'disabled:opacity-50 disabled:cursor-not-allowed',
					checked ? 'bg-navy' : 'bg-navy-200',
					s.track,
				)}
				{...props}
			>
				<motion.span
					className={cn(
						'rounded-full shadow-sm',
						checked ? 'bg-gold' : 'bg-white',
						s.knob,
					)}
					animate={{ x: checked ? s.translate : 0 }}
					transition={{ type: 'spring', stiffness: 500, damping: 30 }}
				/>
			</button>
			{(label || hint) && (
				<div className="flex flex-col">
					{label && (
						<label htmlFor={id} className="text-sm font-medium font-ui text-foreground cursor-pointer">
							{label}
						</label>
					)}
					{hint && (
						<span className="text-xs text-muted-foreground font-ui">{hint}</span>
					)}
				</div>
			)}
		</div>
	)
}
